import { resolveMemberId } from '../../shared/default-member-id.js';
import { flipMiSheberachCard } from '../msc-card-3d.js';

const DEFAULT_COLSPAN = 10;

export default {
    paymentDataRoute: null,
    pledgeExpandContentRoute: null,
    memberExpandContentRoute: null,
    memberDetailFragmentRoute: null,
    memberExpandPanelFragmentsRoute: null,
    detailListPartialRoute: null,
    relationshipsPartialRoute: null,
    sourcePage: '',
    portal: 'manager',
    paymentUserType: null,
    isToggling: false,
    callbacks: {},
    rowActionHandlers: null,
    activeRequests: {},
    ANIMATION_DURATION: 300,
    TOGGLE_DEBOUNCE: 350,
    COLSPAN: DEFAULT_COLSPAN,
    RELATIONSHIPS_DISPLAY_LIMIT: 10,
    loadingButton: null,
    loadingTimeoutId: null,
    LOADER_CLEAR_MS: 8000,

    managerAccessLevels() {
        return window.__MANAGER_NAV_CONFIG__?.access?.levels || {};
    },

    canEditCapability(capability) {
        if (this.portal !== 'manager') {
            return true;
        }
        if (typeof window.managerCanWriteCapability === 'function') {
            return window.managerCanWriteCapability(capability);
        }
        const required = String(capability || '').trim();
        if (required === '') {
            return false;
        }
        return this.managerAccessLevels()[required] === 'edit';
    },

    canAccessCapability(capability) {
        if (this.portal !== 'manager') {
            return true;
        }
        const required = String(capability || '').trim();
        if (required === '') {
            return true;
        }
        const level = this.managerAccessLevels()[required];
        return level === 'read' || level === 'edit';
    },

    canEditMembers() {
        if (this.portal !== 'manager') {
            return true;
        }
        return this.canEditCapability('members');
    },

    delegationRootId: null,
    gridId: null,

    getGridDelegationRoot() {
        const rootId = this.delegationRootId;
        if (!rootId) {
            throw new Error('getGridDelegationRoot: delegationRootId is required');
        }
        const root = document.getElementById(rootId);
        if (!root) {
            throw new Error('getGridDelegationRoot: #' + rootId + ' is required');
        }
        return root;
    },

    attachRowActionClickHandler() {
        if (!this.rowActionHandlers) {
            throw new Error('attachRowActionClickHandler: rowActionHandlers is required');
        }
        const delegateRoot = this.getGridDelegationRoot();
        const expander = this;
        const $root = $(delegateRoot);
        $root.off('click.pledgesRowAction', '[data-pledges-row-action]')
            .on('click.pledgesRowAction', '[data-pledges-row-action]', function (event) {
            event.preventDefault();
            const $btn = $(this);
            const action = $btn.attr('data-pledges-row-action');
            if (!action) {
                throw new Error('attachRowActionClickHandler: data-pledges-row-action is required');
            }
            const handler = expander.rowActionHandlers[action];
            if (typeof handler !== 'function') {
                throw new Error('attachRowActionClickHandler: ' + action + ' is required');
            }
            handler($btn);
        });
    },

    init(opts) {
        const options = opts || {};
        this.paymentDataRoute = options.paymentDataRoute || null;
        this.pledgeExpandContentRoute = options.pledgeExpandContentRoute || null;
        this.memberExpandContentRoute = options.memberExpandContentRoute || null;
        this.memberDetailFragmentRoute = options.memberDetailFragmentRoute || null;
        this.memberExpandPanelFragmentsRoute = options.memberExpandPanelFragmentsRoute || null;
        this.detailListPartialRoute = options.detailListPartialRoute || null;
        this.relationshipsPartialRoute = options.relationshipsPartialRoute || null;
        this.sourcePage = options.sourcePage || '';
        this.portal = options.portal || 'manager';
        this.paymentUserType = options.paymentUserType == null ? '' : String(options.paymentUserType).trim();
        this.callbacks = options.callbacks || {};
        this.rowActionHandlers = options.rowActionHandlers || null;
        this.delegationRootId = options.delegationRootId || null;
        this.gridId = options.gridId || null;
        this.useDivGrid = options.useDivGrid === true;
        this.defaultMemberId = resolveMemberId(options.defaultMemberId);
        this.COLSPAN = options.colspan != null ? options.colspan : DEFAULT_COLSPAN;
        this.isToggling = false;
        this.attachClickHandler();
        if (this.rowActionHandlers) {
            this.attachRowActionClickHandler();
        }
        this.attachExpandContentDelegation();
        this.initTooltips();
    },

    initTooltips() {
        if (this._tooltipsInitialized) return;
        this._tooltipsInitialized = true;
        $(document).tooltip({ selector: '[data-toggle="tooltip"]' });
    },

    isExpandActionBlocked($el, action) {
        if (action === 'add-credit-funds') {
            return !this.canEditCapability('pledges');
        }
        if (action === 'view-credit-details') {
            return !this.canAccessCapability('pledges');
        }
        if (['edit-member', 'open-mi-sheberach-card', 'add-pledge', 'add-relationship', 'send-statement', 'print-statement'].includes(action)) {
            if (!this.canEditMembers()) {
                return true;
            }
        }
        return $el.hasClass('disabled') || $el.attr('aria-disabled') === 'true' || $el.prop('disabled') === true;
    },

    dispatchExpandContentAction($el) {
        const action = $el.data('action');
        if (!action || this.isExpandActionBlocked($el, action)) {
            return;
        }
        switch (action) {
            case 'view-member': {
                const memberId = $el.data('member-id');
                if (memberId != null) this.callCallback('openViewModal', memberId);
                break;
            }
            case 'view-payments': {
                const pledgeId = $el.data('pledge-id');
                if (pledgeId != null) this.callCallback('OpenViewRecurPayments', pledgeId, $el.data('table-name') || '');
                break;
            }
            case 'view-all-pledges': {
                const memberId = $el.data('member-id');
                if (memberId != null) this.callCallback('viewAllMemberPledge', memberId, this.sourcePage);
                break;
            }
            case 'add-pledge': {
                const memberId = $el.data('member-id');
                if (memberId != null) this.callCallback('openAddPledgeModal', memberId);
                break;
            }
            case 'edit-member': {
                const memberId = $el.data('member-id');
                if (memberId != null) this.callCallback('openEditModal', memberId);
                break;
            }
            case 'add-relationship': {
                const memberId = $el.data('member-id');
                if (memberId != null) this.callCallback('openAddRelationship', memberId);
                break;
            }
            case 'view-relationships': {
                const memberId = $el.data('member-id');
                if (memberId != null) this.callCallback('viewRelationships', memberId);
                break;
            }
            case 'open-mi-sheberach-card': {
                const memberId = $el.data('member-id');
                if (memberId != null) this.callCallback('openMiSheberachCard', memberId);
                break;
            }
            case 'view-mi-sheberach-card': {
                const memberId = $el.data('member-id');
                if (memberId != null) this.callCallback('viewMiSheberachCard', memberId);
                break;
            }
            case 'flip-mi-sheberach-card': {
                flipMiSheberachCard($el.closest('.member-expand-mi-sheberach-fragment-root')[0]);
                break;
            }
            case 'download-mi-sheberach-card': {
                const memberId = $el.data('member-id');
                if (memberId != null) this.callCallback('downloadMiSheberachCard', memberId);
                break;
            }
            case 'view-credit-details': {
                const memberId = $el.data('member-id');
                if (memberId != null) this.callCallback('openMemberOpenCredits', memberId);
                break;
            }
            case 'add-credit-funds': {
                const memberId = $el.data('member-id');
                if (memberId != null) this.callCallback('addOpenCreditFunds', memberId);
                break;
            }
            case 'view-profile': {
                const memberId = $el.data('member-id');
                if (memberId != null) this.callCallback('openMemberProfilePage', memberId);
                break;
            }
            case 'send-statement': {
                this.callCallback('openInvoiceModal', $el.data('pledge-ids') || '');
                break;
            }
            case 'print-statement': {
                const memberId = $el.data('member-id');
                this.callCallback('openPrintStatementModal', $el.data('pledge-ids') || '', memberId);
                break;
            }
            default:
                break;
        }
    },

    attachExpandContentDelegation() {
        const expander = this;
        const delegateRoot = this.getGridDelegationRoot();
        const selector = '[data-expand-content] [data-action], .data-grid-mobile-actions-buttons [data-action]';
        const $root = $(delegateRoot);
        $root.off('click.pledgeExpandContent');
        $root.on('click.pledgeExpandContent', selector, function (e) {
            e.preventDefault();
            e.stopPropagation();
            expander.dispatchExpandContentAction($(e.currentTarget));
        });
    },
};
